<template>
  <div v-if="show" class="otp-dialog-overlay" @click="handleOverlayClick">
    <div class="otp-dialog" @click.stop>
      <div class="otp-dialog__header">
        <h3 class="otp-dialog__title">
          {{ $t('notifications.verifyAccess') }}
        </h3>
        <button 
          @click="handleClose" 
          class="otp-dialog__close-btn"
          :title="$t('common.close')"
          type="button"
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
          </svg>
        </button>
      </div>

      <div class="otp-dialog__content">
        <div class="otp-dialog__info">
          <div class="otp-dialog__icon">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.18L16.59,7.59L18,9L10,17Z" />
            </svg>
          </div>
          <div class="otp-dialog__message">
            <p>{{ $t('notifications.otpInstructions') }}</p>
            <p class="otp-dialog__project-name">
              <strong>{{ notification?.data?.projectName }}</strong>
            </p>
          </div>
        </div>

        <!-- OTP Input -->
        <div class="otp-dialog__input-section">
          <label for="otpCode" class="otp-dialog__label">
            {{ $t('notifications.securityCode') }}
          </label>
          <input
            id="otpCode"
            ref="otpInput"
            v-model="otpCode"
            type="text"
            class="otp-dialog__input"
            :class="{ 'otp-dialog__input--error': !!errorMessage }"
            :placeholder="$t('notifications.enterCodePlaceholder')"
            maxlength="6"
            autocomplete="off"
            @input="handleOtpInput"
            @keydown.enter="handleVerify"
          />
          <div v-if="errorMessage" class="otp-dialog__error">
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div class="otp-dialog__footer">
        <button 
          @click="handleClose" 
          class="app-button app-button--outline"
          type="button"
          :disabled="isLoading"
        >
          {{ $t('common.cancel') }}
        </button>
        <button 
          @click="handleVerify" 
          class="app-button app-button--success"
          type="button"
          :disabled="!otpCode.trim() || isLoading"
        >
          <svg v-if="isLoading" viewBox="0 0 24 24" width="16" height="16" class="spinning">
            <path fill="currentColor" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
          </svg>
          <svg v-else viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z" />
          </svg>
          {{ $t('notifications.verify') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-ignore
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  show: boolean;
  notification: any;
  isLoading: boolean;
}

interface Emits {
  close: [];
  verify: [data: { notification: any; otp: string }];
}

export default {
  name: 'OtpVerificationDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    notification: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'verify'],
  setup(props: Props, { emit }: any) {
    const { t } = useI18n();

    const otpInput = ref(null);
    const otpCode = ref('');
    const errorMessage = ref('');

    // Watch for dialog show/hide to focus input and reset state
    watch(() => props.show, (newValue: boolean) => {
      if (newValue) {
        // Reset state when dialog opens
        otpCode.value = '';
        errorMessage.value = '';
        
        // Focus the input field
        nextTick(() => {
          if (otpInput.value) {
            otpInput.value.focus();
          }
        });
      }
    });

    // Clear error when user types
    const handleOtpInput = () => {
      errorMessage.value = '';
      // Only allow numbers
      otpCode.value = otpCode.value.replace(/\D/g, '');
    };

    const handleClose = () => {
      if (!props.isLoading) {
        emit('close' as any);
      }
    };

    const handleOverlayClick = () => {
      handleClose();
    };

    const handleVerify = () => {
      if (!otpCode.value.trim()) {
        errorMessage.value = t('notifications.otpRequired');
        return;
      }

      if (otpCode.value.length !== 6) {
        errorMessage.value = t('notifications.otpMustBeSixDigits');
        return;
      }

      emit('verify', {
        notification: props.notification,
        otp: otpCode.value.trim()
      });
    };

    return {
      otpInput,
      otpCode,
      errorMessage,
      handleOtpInput,
      handleClose,
      handleOverlayClick,
      handleVerify
    };
  }
};
</script>

<style scoped>
.otp-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.otp-dialog {
  background: var(--bg-primary, #ffffff);
  border-radius: var(--border-radius-lg, 8px);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.otp-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 0;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.otp-dialog__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
}

.otp-dialog__close-btn {
  background: none;
  border: none;
  padding: 0.25rem;
  border-radius: var(--border-radius-sm, 4px);
  color: var(--text-secondary, #6b7280);
  cursor: pointer;
  transition: all 0.2s ease;
}

.otp-dialog__close-btn:hover {
  background: var(--bg-secondary, #f3f4f6);
  color: var(--text-primary, #111827);
}

.otp-dialog__content {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.otp-dialog__info {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.otp-dialog__icon {
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  background: var(--success-bg, #dcfce7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--success, #16a34a);
}

.otp-dialog__message {
  flex: 1;
}

.otp-dialog__message p {
  margin: 0 0 0.5rem 0;
  color: var(--text-secondary, #6b7280);
  line-height: 1.5;
}

.otp-dialog__project-name {
  color: var(--text-primary, #111827) !important;
  font-weight: 500;
}

.otp-dialog__input-section {
  margin-bottom: 1rem;
}

.otp-dialog__label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary, #111827);
}

.otp-dialog__input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: var(--border-radius, 6px);
  font-size: 1rem;
  transition: all 0.2s ease;
  text-align: center;
  letter-spacing: 0.2em;
  font-family: 'Courier New', monospace;
  font-weight: 600;
}

.otp-dialog__input:focus {
  outline: none;
  border-color: var(--primary, #3b82f6);
  box-shadow: 0 0 0 3px var(--primary-alpha, rgba(59, 130, 246, 0.1));
}

.otp-dialog__input--error {
  border-color: var(--error, #ef4444);
}

.otp-dialog__input--error:focus {
  border-color: var(--error, #ef4444);
  box-shadow: 0 0 0 3px var(--error-alpha, rgba(239, 68, 68, 0.1));
}

.otp-dialog__error {
  margin-top: 0.5rem;
  color: var(--error, #ef4444);
  font-size: 0.875rem;
}

.otp-dialog__footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.app-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid transparent;
  border-radius: var(--border-radius, 6px);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 2.5rem;
}

.app-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.app-button--outline {
  background: transparent;
  color: var(--text-secondary, #6b7280);
  border-color: var(--border-color, #d1d5db);
}

.app-button--outline:hover:not(:disabled) {
  background: var(--bg-secondary, #f3f4f6);
  color: var(--text-primary, #111827);
}

.app-button--success {
  background: var(--success, #16a34a);
  color: white;
}

.app-button--success:hover:not(:disabled) {
  background: var(--success-dark, #15803d);
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive design */
@media (max-width: 640px) {
  .otp-dialog {
    margin: 0;
    width: 100%;
    max-width: none;
    border-radius: 0;
  }

  .otp-dialog__header,
  .otp-dialog__content,
  .otp-dialog__footer {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .otp-dialog__info {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}
</style>
